import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import noop from 'lodash/noop';
import Loading from '@components-clean/core/loading/loading';
import { useGetCountriesForStudy } from '@apis/bff-api/central-monitoring';

type DropdownItem = {
    value: string | undefined;
    text: string;
};
const DefaultItem: DropdownItem = {
    value: undefined,
    text: 'No Country Filter',
};

export type CountrySelectorState = {
    country: DropdownItem;
    setCountry: (value: DropdownItem) => void;
    countryList: DropdownItem[];
    isLoading: boolean;
};
export const CountrySelectorContext = createContext<CountrySelectorState>({
    country: DefaultItem,
    setCountry: noop,
    countryList: [DefaultItem],
    isLoading: true,
});

interface Props {
    studyRef: string;
    children: ReactNode;
}

export const CountrySelectorContextProvider = ({ studyRef, children }: Props) => {
    const { data: countries, isLoading } = useGetCountriesForStudy(studyRef);

    const countryList: DropdownItem[] = countries
        ? [
              DefaultItem,
              ...countries
                  .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                  .map((c) => ({ value: c, text: c })),
          ]
        : [];

    const [country, setCountry] = useState<DropdownItem>(DefaultItem);
    // We'll use this state to track if we've successfully loaded countries/default selection (from search params) to
    // prevent extra data fetching
    const [hasLoaded, setHasLoaded] = useState<Boolean>(false);

    useEffect(() => {
        if (countries === undefined) return;
        let defaultSelection = DefaultItem;

        const query = new URLSearchParams(location.search);
        const countryQuery = query.get('country');
        if (countryQuery) defaultSelection = countryList.find((x) => x.value === countryQuery) || DefaultItem;

        setHasLoaded(true);
        setCountry(defaultSelection);
    }, [countries]);

    return (
        <CountrySelectorContext.Provider
            value={useMemo(
                () => ({
                    country,
                    setCountry,
                    countryList,
                    isLoading,
                }),
                [studyRef, country, isLoading, countryList],
            )}
        >
            {/* If loading, avoid rendering children to prevent data fetches that might not be needed until the default country is selected */}
            {isLoading || !hasLoaded ? <Loading /> : children}
        </CountrySelectorContext.Provider>
    );
};

export const useCountrySelectorContext = () => useContext(CountrySelectorContext);
